import { useClaims } from '@backoffice-frontend/authentication'
import { formatDateTime, getLanguage } from '@backoffice-frontend/common'
import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { useGetServiceAreasQuery } from '@backoffice-frontend/service-area-picker'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { Accordion, LoadingIndicator, Tag } from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountLayout } from './components/AccountLayout'
import { usePermissionsViewQuery } from './PermissionsView.hook'

type TagGroupProps<T> = {
  items: T[] | null | undefined
  getKey: (item: T) => string
  getLabel: (item: T) => string
}

const TagGroup = <T,>({ items, getKey, getLabel }: TagGroupProps<T>) => {
  return (
    <div
      css={theme => ({
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.space.Space1,
        maxHeight: 24 * 12,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
      })}
    >
      {items?.map(item => (
        <Tag key={getKey(item)} label={getLabel(item)} />
      ))}
    </div>
  )
}

export const PermissionsView = () => {
  const { data: serviceAreasData } = useGetServiceAreasQuery()
  const { t, i18n } = useTranslation()

  const { user, loading: groupsLoading } = useBackofficeLoggedInUser()
  const claims = useClaims()

  const { data, loading: queryLoading } = useErrorHandlingQuery(
    usePermissionsViewQuery,
    { variables: { email: claims.cognito.email() } },
  )

  const filteredServiceAreas = serviceAreasData?.serviceAreas.filter(it =>
    claims.boum.serviceAreas().includes(it.uuid),
  )

  const [rights, setRights] = useState<string[]>([])
  useEffect(() => {
    setRights(claims.boum.userRights().toJSON().sort())
  }, [claims])

  const groupsApprover = user?.groupsApprover
  const { customerTenants, operatorTenants, ridepoolingServiceTenants } =
    claims.boum

  const resolveGroupDisplayName = (groupName: string) =>
    data?.groups?.find(el => el.name === groupName)?.displayName ?? groupName

  return (
    <AccountLayout
      header={t('authenticatedAppView_permissions_header')}
      subheader={t('authenticatedAppView_permissions_caption')}
    >
      <div>
        <Accordion
          title={t('authenticatedAppView_yourOperatorTenants')}
          compact
          disabled={operatorTenants().length === 0}
        >
          <TagGroup
            items={operatorTenants()}
            getKey={tenant => tenant}
            getLabel={tenant => tenant}
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourCustomerTenants')}
          compact
          disabled={customerTenants().length === 0}
        >
          <TagGroup
            items={customerTenants()}
            getKey={tenant => tenant}
            getLabel={tenant => tenant}
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourRidepoolingServiceTenants')}
          compact
          disabled={ridepoolingServiceTenants().length === 0}
        >
          <TagGroup
            items={ridepoolingServiceTenants()}
            getKey={tenant => tenant}
            getLabel={tenant => tenant}
          />
        </Accordion>

        {!queryLoading && (
          <Accordion
            title={t('authenticatedAppView_yourFleets')}
            compact
            disabled={data?.fleets?.length === 0}
          >
            <TagGroup
              items={data?.fleets}
              getKey={fleet => fleet.id}
              getLabel={fleet => fleet.name}
            />
          </Accordion>
        )}

        <Accordion
          title={t('authenticatedAppView_yourServiceAreas')}
          compact
          disabled={filteredServiceAreas?.length === 0}
        >
          <TagGroup
            items={filteredServiceAreas}
            getKey={serviceArea => serviceArea.uuid}
            getLabel={serviceArea =>
              serviceArea.displayNames[getLanguage(i18n.language)] ??
              serviceArea.id
            }
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourGroups')}
          compact
          disabled={user?.timedGroups.length === 0}
        >
          {groupsLoading ? (
            <LoadingIndicator />
          ) : (
            <TagGroup
              items={user?.timedGroups}
              getKey={group => group.groupName}
              getLabel={group =>
                `${resolveGroupDisplayName(group.groupName)}${
                  group.expires
                    ? ` (${t('common_expires_label')} ${formatDateTime(group.expires)})`
                    : ''
                }`
              }
            />
          )}
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourRights')}
          compact
          disabled={rights.length === 0}
        >
          <TagGroup
            items={rights}
            getKey={right => right}
            getLabel={right => right}
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_groupsYouApprove')}
          compact
          disabled={groupsApprover?.length === 0}
        >
          <TagGroup
            items={groupsApprover}
            getKey={group => group}
            getLabel={group => resolveGroupDisplayName(group)}
          />
        </Accordion>
      </div>
    </AccountLayout>
  )
}
